import React, { useState, useEffect } from 'react';
import { Form, Input, Select, DatePicker, Button, Table, Tag, Space, message, Col } from 'antd';
import { useTranslation } from 'react-i18next';
import { useTableScrollHeight } from '@/hooks/useTableScrollHeight';
import dayjs from 'dayjs';
import FilterPanel from '../../../components/common/FilterPanel';
import type { WaveInstanceDTO, QueryWaveInstancePageRequest } from '../../../types/wave/waveInstance';
import type { Paging } from '../../../types/common';
import * as waveInstanceService from '../../../services/wave/waveInstanceService';
import {
  WaveStatusOptions,
} from '../../../constants/wave';
import WaveInstanceDetail from './components/WaveInstanceDetail';

const { RangePicker } = DatePicker;

const WaveInstanceList: React.FC = () => {
  const { t } = useTranslation();
  const { tableWrapRef, scrollY } = useTableScrollHeight();
  const [searchForm] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [dataSource, setDataSource] = useState<WaveInstanceDTO[]>([]);
  const [pagination, setPagination] = useState<Paging>({ currentPage: 1, pageSize: 10, totalCount: 0 });
  
  // Drawer states
  const [detailVisible, setDetailVisible] = useState(false);
  const [viewingWaveNo, setViewingWaveNo] = useState<string | null>(null);

  // Load data
  const loadData = async (params: QueryWaveInstancePageRequest) => {
    setLoading(true);
    try {
      const response = await waveInstanceService.pageWaveInstances(params);
      if (response.success && response.data) {
        setDataSource(response.data.tableData);
        setPagination(response.data.paging);
      } else {
        message.error(response.errorMessage || t('common.message.loadFailed'));
      }
    } catch (error) {
      message.error(t('common.message.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  // Initialize
  useEffect(() => {
    loadData({ currentPage: 1, pageSize: 10 });
  }, []);

  // Search
  const handleSearch = () => {
    const values = searchForm.getFieldsValue();
    const scheduleTimeRange = values.scheduleTimeRange;
    const params: QueryWaveInstancePageRequest = {
      currentPage: 1,
      pageSize: pagination.pageSize,
      waveNo: values.waveNo,
      waveStatus: values.waveStatus,
      orderNo: values.orderNo,
      scheduleTimeStart: scheduleTimeRange?.[0] ? scheduleTimeRange[0].valueOf() : undefined,
      scheduleTimeEnd: scheduleTimeRange?.[1] ? scheduleTimeRange[1].valueOf() : undefined,
    };
    loadData(params);
  };

  // Reset
  const handleReset = () => {
    searchForm.resetFields();
    loadData({ currentPage: 1, pageSize: pagination.pageSize });
  };

  // Pagination change
  const handlePageChange = (page: number, pageSize: number) => {
    const values = searchForm.getFieldsValue();
    const scheduleTimeRange = values.scheduleTimeRange;
    const params: QueryWaveInstancePageRequest = {
      currentPage: page,
      pageSize,
      waveNo: values.waveNo,
      waveStatus: values.waveStatus,
      orderNo: values.orderNo,
      scheduleTimeStart: scheduleTimeRange?.[0] ? scheduleTimeRange[0].valueOf() : undefined,
      scheduleTimeEnd: scheduleTimeRange?.[1] ? scheduleTimeRange[1].valueOf() : undefined,
    };
    loadData(params);
  };

  // View detail
  const handleViewDetail = (record: WaveInstanceDTO) => {
    setViewingWaveNo(record.waveNo);
    setDetailVisible(true);
  };

  // Format datetime
  const formatDateTime = (timestamp: number | null) => {
    return timestamp ? dayjs(timestamp).format('DD-MM-YYYY HH:mm:ss') : '-';
  };

  // Render status tag
  const renderStatusTag = (status: string) => {
    const option = WaveStatusOptions.find(opt => opt.value === status);
    if (!option) return status;
    return <Tag color={option.color}>{t(option.label)}</Tag>;
  };

  // Table columns
  const columns = [
    {
      title: t('wave.waveInstance.list.table.waveNo'),
      dataIndex: 'waveNo',
      key: 'waveNo',
      width: 160,
      fixed: 'left' as const,
      render: (text: string, record: WaveInstanceDTO) => (
        <a onClick={() => handleViewDetail(record)}>{text}</a>
      ),
    },
    {
      title: t('wave.waveInstance.list.table.waveStatus'),
      dataIndex: 'waveStatus',
      key: 'waveStatus',
      width: 130,
      render: renderStatusTag,
    },
    {
      title: t('wave.waveInstance.list.table.successOrderCount'),
      dataIndex: 'successOrderCount',
      key: 'successOrderCount',
      width: 130,
    },

    {
      title: t('wave.waveInstance.list.table.createTime'),
      dataIndex: 'gmtCreate',
      key: 'gmtCreate',
      width: 180,
      render: formatDateTime,
    },
    {
      title: t('wave.waveInstance.list.table.actions'),
      key: 'actions',
      width: 120,
      fixed: 'right' as const,
      render: (_: any, record: WaveInstanceDTO) => (
        <Space size="small">
          <Button type="link" size="small" onClick={() => handleViewDetail(record)}>
            {t('wave.waveInstance.list.actions.detail')}
          </Button>
        </Space>
      ),
    },
  ];

  // Search form fields
  const renderSearchFields = () => (
    <>
      <Col span={6}>
        <Form.Item name="waveNo" label={t('wave.waveInstance.list.search.waveNo')}>
          <Input
            placeholder={t('wave.waveInstance.list.search.placeholder.waveNo')}
            allowClear
          />
        </Form.Item>
      </Col>
      <Col span={6}>
        <Form.Item name="waveStatus" label={t('wave.waveInstance.list.search.waveStatus')}>
          <Select
            placeholder={t('wave.waveInstance.list.search.placeholder.waveStatus')}
            allowClear
            options={WaveStatusOptions.map(opt => ({
              label: t(opt.label),
              value: opt.value,
            }))}
          />
        </Form.Item>
      </Col>
      <Col span={6}>
        <Form.Item name="orderNo" label={t('wave.waveInstance.list.search.orderNo')}>
          <Input
            placeholder={t('wave.waveInstance.list.search.placeholder.orderNo')}
            allowClear
          />
        </Form.Item>
      </Col>
      <Col span={6}>
        <Form.Item name="scheduleTimeRange" label={t('wave.waveInstance.list.search.scheduleTime')}>
          <RangePicker
            showTime
            style={{ width: '100%' }}
            placeholder={[
              t('wave.waveInstance.list.search.placeholder.scheduleTime'),
              t('wave.waveInstance.list.search.placeholder.scheduleTime'),
            ]}
          />
        </Form.Item>
      </Col>
    </>
  );

  return (
    <div style={{ padding: '16px', height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Search Panel */}
      <FilterPanel
        form={searchForm}
        onFilter={handleSearch}
        onReset={handleReset}
        loading={loading}
      >
        {renderSearchFields()}
      </FilterPanel>

      {/* Table */}
      <div ref={tableWrapRef} style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
        <Table
          rowKey="waveNo"
          columns={columns}
          dataSource={dataSource}
          loading={loading}
          scroll={{ x: 1400, y: scrollY }}
          pagination={{
            current: pagination.currentPage,
            pageSize: pagination.pageSize,
            total: pagination.totalCount,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => t('common.pagination.total', { total }),
            onChange: handlePageChange,
          }}
        />
      </div>

      {/* Detail Drawer */}
      {detailVisible && viewingWaveNo && (
        <WaveInstanceDetail
          visible={detailVisible}
          waveNo={viewingWaveNo}
          onClose={() => setDetailVisible(false)}
        />
      )}
    </div>
  );
};

export default WaveInstanceList;
